import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';
import { credentialTemplateSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface TemplateDetail {
  id: string;
  name: string;
  createdAt: string;
  credentialsCount: number;
}

const LIST_URL = '/credenciales/plantillas';

function useTemplate() {
  const { id } = useParams<{ id: string }>();
  const query = useQuery({
    queryKey: ['credentials', 'templates', id],
    queryFn: () => api<TemplateDetail>(`/credentials/templates/${id}`),
    enabled: Boolean(id),
  });
  return { id, ...query };
}

function formatCreated(value: string) {
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
}

export function CredentialTemplateShowPage() {
  const { id, data, isLoading, error } = useTemplate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [name, setName] = useState('');

  useEffect(() => {
    if (data) setName(data.name);
  }, [data]);

  const mutation = useMutation({
    mutationFn: (value: string) => api(`/credentials/templates/${id}`, { method: 'PATCH', body: { name: value } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['credentials', 'templates'] });
      await queryClient.invalidateQueries({ queryKey: ['credential-templates'] });
      toast(credentialTemplateSuccessToast('edit'));
    },
    onError: (e) => toast(roleActionErrorToast(e, 'No se pudo guardar la plantilla de credencial')),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (name.trim()) mutation.mutate(name.trim());
  }

  return (
    <div className="lms-credentials-issue space-y-6 font-sans">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex items-start gap-4">
          <HubBackLink to={LIST_URL} label="Volver a plantillas" />
          <div>
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">{data?.name ?? 'Plantilla'}</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">Detalle de la plantilla de credencial</p>
          </div>
        </div>
        <Link
          to={`${LIST_URL}/${id}/editor`}
          className="inline-flex items-center gap-2 rounded-lg bg-[#000256] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-[#001863] active:scale-[0.99]"
        >
          <span className="material-symbols-outlined text-[19px]" aria-hidden="true">
            palette
          </span>
          Abrir editor
        </Link>
      </div>
      {error && (
        <p className="text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        data && (
          <>
            <div className="space-y-6 rounded-xl border border-[#e2e2e5] bg-white p-6 shadow-sm">
              <div>
                <h2 className="text-lg font-bold text-[#000256]">Propiedades de la plantilla</h2>
                <p className="mt-1 text-sm text-gray-500">Configura el identificador público y los metadatos institucionales asociados.</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="template-name" className="mb-1.5 block text-sm font-semibold text-gray-800">
                    Nombre <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="template-name"
                    required
                    value={name}
                    placeholder="Ingresa el nombre descriptivo..."
                    onChange={(e) => setName(e.target.value)}
                    className="h-10 w-full rounded-lg border border-gray-300 px-3.5 text-sm text-gray-900 shadow-sm outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-[#000256]"
                  />
                </div>
                <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={mutation.isPending || !name.trim()}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[#000256] px-4 text-sm font-medium text-white shadow-sm transition-all hover:bg-[#001863] active:scale-[0.98] disabled:opacity-60"
                >
                  <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
                    check
                  </span>
                  {mutation.isPending ? 'Guardando...' : 'Guardar nombre'}
                </button>
                </div>
              </form>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex items-center gap-4 rounded-xl border border-[#e2e2e5] bg-white p-4 shadow-sm sm:p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-50 text-[#00227B]">
                  <span className="material-symbols-outlined text-[22px]" aria-hidden="true">
                    calendar_today
                  </span>
                </div>
                <div>
                  <span className="block text-xs font-semibold uppercase tracking-wider text-gray-500">Creada</span>
                  <span className="text-base font-bold text-gray-900">{formatCreated(data.createdAt)}</span>
                </div>
              </div>
              <div className="flex items-center gap-4 rounded-xl border border-[#e2e2e5] bg-white p-4 shadow-sm sm:p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-50 text-[#00227B]">
                  <span className="material-symbols-outlined text-[22px]" aria-hidden="true">
                    workspace_premium
                  </span>
                </div>
                <div>
                  <span className="block text-xs font-semibold uppercase tracking-wider text-gray-500">Credenciales emitidas</span>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-gray-900">{data.credentialsCount}</span>
                    {data.credentialsCount === 0 && (
                      <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">Sin emisiones</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}

export { CredentialTemplateEditPage } from './CredentialTemplateEditorPage';
